import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { ComplianceBadge } from '../components/ComplianceBadge';
import { formatAUD, formatDateAU } from '../../../shared/format';
import { useNamedViews } from '../hooks/useSavedView';
import { SavedViewsMenu } from '../components/SavedViewsMenu';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';
import { QAChartTooltip } from '../components/QAChartTooltip';
import type { DashboardRow, DashboardSummary } from '../../../shared/types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';

interface View {
  q: string;
  segment: string;
  rpFilter: string;
  outstandingOnly: boolean;
  sort: { key: string; dir: 'asc' | 'desc' };
}

const DEFAULT_VIEW: View = { q: '', segment: '', rpFilter: '', outstandingOnly: false, sort: { key: 'name', dir: 'asc' } };

const QA_COLOURS = ['#B8431A', '#A07A3F', '#2F4636', '#8A6418', '#7A6E63'];

export function Dashboard() {
  const { user } = useAuth();
  const viewApi = useNamedViews<View>('dashboard', DEFAULT_VIEW);
  const view = viewApi.value;
  const setView = viewApi.setValue;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<{ data: DashboardRow[]; summary: DashboardSummary }>('/api/dashboard'),
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.data.filter(r =>
      (!view.q ||
        r.name.toLowerCase().includes(view.q.toLowerCase()) ||
        r.emails.some(e => e?.toLowerCase().includes(view.q.toLowerCase()))) &&
      (!view.segment || r.segment === view.segment) &&
      (!view.outstandingOnly || Object.values(r.compliance).some(v => v === 'outstanding')),
    );
    // sort
    rows = [...rows].sort((a, b) => {
      const dir = view.sort.dir === 'asc' ? 1 : -1;
      const k = view.sort.key as keyof DashboardRow;
      const av = a[k] as unknown as string | number | null;
      const bv = b[k] as unknown as string | number | null;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
    });
    return rows;
  }, [data, view]);

  const segmentChart = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, number>();
    for (const r of data.data) {
      const seg = r.segment ?? 'unsegmented';
      map.set(seg, (map.get(seg) ?? 0) + 1);
    }
    return [...map.entries()].map(([name, value]) => ({ name, value }));
  }, [data]);

  const outstandingChart = useMemo(() => {
    if (!data) return [];
    const o = data.summary.outstandingCount;
    return [
      { name: 'Risk profile', count: o.riskProfile ?? 0 },
      { name: 'Fact find', count: o.factFind ?? 0 },
      { name: 'File note', count: o.fileNote ?? 0 },
      { name: 'Best interest', count: o.bestInterest ?? 0 },
      { name: 'SoA signed', count: o.soaSigned ?? 0 },
      { name: 'Fee consent', count: o.feeConsent ?? 0 },
      { name: 'Write SoA', count: o.writeSoa ?? 0 },
      { name: 'Write RoA', count: o.writeRoa ?? 0 },
    ];
  }, [data]);

  if (isLoading) return <QASkeleton variant="dashboard" />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load dashboard</div>;

  function toggleSort(key: string) {
    setView(v => ({ ...v, sort: { key, dir: v.sort.key === key && v.sort.dir === 'asc' ? 'desc' : 'asc' } }));
  }

  return (
    <div>
      <div className="mb-8 qa-rise qa-stagger-1">
        <p className="qa-eyebrow mb-2">Adviser dashboard</p>
        <h1 className="qa-h1">
          Your book, <span className="qa-italic-accent">today.</span>
        </h1>
        <p className="qa-deck mt-2 text-ink-soft">
          {data.summary.clientCount} clients · {formatAUD(data.summary.totalTnw, { compact: true })} total net worth · {formatAUD(data.summary.totalCash, { compact: true })} in cash
        </p>
      </div>

      {/* Admin attention banner — shows when there are unlinked file-notes
          or open duplicate-match candidates. Hidden when nothing's pending or
          the user doesn't have `users.manage`. */}
      {can(user?.role, 'users.manage') && (
        (data.summary.attentionCounts?.unlinkedFileNotes ?? 0) > 0 ||
        (data.summary.attentionCounts?.openMatchCandidates ?? 0) > 0
      ) && (
        <div className="qa-attention-banner mb-6 qa-rise qa-stagger-1">
          <span className="qa-eyebrow text-xs text-caution">Needs attention</span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-1">
            {(data.summary.attentionCounts?.unlinkedFileNotes ?? 0) > 0 && (
              <Link to="/admin/unlinked-file-notes"
                className="text-sm text-ink hover:text-rust underline decoration-hairline underline-offset-4">
                <span className="qa-mono">{data.summary.attentionCounts!.unlinkedFileNotes}</span>
                {' '}unlinked file note{data.summary.attentionCounts!.unlinkedFileNotes === 1 ? '' : 's'} →
              </Link>
            )}
            {(data.summary.attentionCounts?.openMatchCandidates ?? 0) > 0 && (
              <Link to="/admin/match-candidates"
                className="text-sm text-ink hover:text-rust underline decoration-hairline underline-offset-4">
                <span className="qa-mono">{data.summary.attentionCounts!.openMatchCandidates}</span>
                {' '}open duplicate match{data.summary.attentionCounts!.openMatchCandidates === 1 ? '' : 'es'} →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 border-t border-b border-hairline mb-8 qa-rise qa-stagger-2">
        <div className="qa-stat">
          <span className="qa-stat__label">Clients</span>
          <span className="qa-stat__value">{data.summary.clientCount}</span>
        </div>
        <div className="qa-stat">
          <span className="qa-stat__label">Total Net Worth</span>
          <span className="qa-stat__value qa-mono">{formatAUD(data.summary.totalTnw, { compact: true })}</span>
        </div>
        <div className="qa-stat">
          <span className="qa-stat__label">Cash</span>
          <span className="qa-stat__value qa-mono">{formatAUD(data.summary.totalCash, { compact: true })}</span>
        </div>
        <div className="qa-stat">
          <span className="qa-stat__label">Reviews due</span>
          <span className="qa-stat__value qa-mono">
            {data.data.filter(r => r.reviewDue && new Date(r.reviewDue) <= new Date()).length}
          </span>
        </div>
        <div className="qa-stat">
          <span className="qa-stat__label">SoA / RoA outstanding</span>
          <span className="qa-stat__value qa-mono">
            {(data.summary.outstandingCount.writeSoa ?? 0) + (data.summary.outstandingCount.writeRoa ?? 0)}
          </span>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 qa-rise qa-stagger-3">
        <div className="qa-card p-5 lg:col-span-2">
          <p className="qa-eyebrow mb-3">Outstanding compliance by rule</p>
          <div className="h-56" aria-hidden="true" inert>
            <ResponsiveContainer>
              <BarChart data={outstandingChart}>
                <CartesianGrid stroke="rgba(26,22,20,0.06)" vertical={false} />
                <XAxis dataKey="name" stroke="#7A6E63" tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#7A6E63' }} />
                <YAxis stroke="#7A6E63" allowDecimals={false} tick={{ fontFamily: 'JetBrains Mono', fontSize: 10, fill: '#7A6E63' }} />
                <Tooltip cursor={{ fill: 'rgba(184,67,26,0.06)' }} content={<QAChartTooltip />} />
                <Bar dataKey="count" name="Outstanding" fill="#B8431A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="qa-card p-5">
          <p className="qa-eyebrow mb-3">Segment mix</p>
          <div className="h-56" aria-hidden="true" inert>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={segmentChart} dataKey="value" nameKey="name" innerRadius={42} outerRadius={70} stroke="#FBF7EF">
                  {segmentChart.map((_, i) => <Cell key={i} fill={QA_COLOURS[i % QA_COLOURS.length]} />)}
                </Pie>
                <Tooltip content={<QAChartTooltip valueFormat={(v) => `${v} client${Number(v) === 1 ? '' : 's'}`} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 items-stretch sm:items-center">
        <input type="search" placeholder="Search by name or email" className="qa-input flex-1"
          value={view.q} onChange={e => setView(v => ({ ...v, q: e.target.value }))} />
        <select aria-label="Filter by segment" className="qa-select sm:w-40" value={view.segment} onChange={e => setView(v => ({ ...v, segment: e.target.value }))}>
          <option value="">All segments</option>
          <option value="A">A</option><option value="B">B</option><option value="C">C</option><option value="D">D</option>
          <option value="inactive">Inactive</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-ink-mute">
          <input type="checkbox" checked={view.outstandingOnly} onChange={e => setView(v => ({ ...v, outstandingOnly: e.target.checked }))} />
          Outstanding only
        </label>
        <SavedViewsMenu api={viewApi} label="View" />
        <button className="btn-ghost text-xs" onClick={() => setView(DEFAULT_VIEW)}>Reset</button>
        <a href="/api/reports/compliance?format=csv" className="btn-ghost text-sm whitespace-nowrap">Export compliance CSV</a>
      </div>

      <div className="text-xs text-ink-mute mb-3">{filtered.length} of {data.data.length} clients</div>

      {/* Grid */}
      <div className="overflow-x-auto border border-hairline qa-rise qa-stagger-4">
        <table className="qa-table min-w-[1100px]">
          <thead>
            <tr>
              <Th label="Client" k="name" view={view} onSort={toggleSort} />
              <Th label="Seg" k="segment" view={view} onSort={toggleSort} />
              <Th label="TNW" k="tnw" view={view} onSort={toggleSort} right />
              <Th label="Cash" k="cash" view={view} onSort={toggleSort} right />
              <Th label="Review" k="reviewDue" view={view} onSort={toggleSort} />
              <Th label="Last note" k="lastFileNote" view={view} onSort={toggleSort} />
              <th>RP</th><th>FF</th><th>FN</th><th>BI</th><th>SoA</th><th>FC</th>
              <th>SoA?</th><th>RoA?</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.clientId}>
                <td>
                  <Link to={`/clients/${r.clientId}`} className="text-ink hover:text-rust font-medium">{r.name}</Link>
                  <div className="text-xs text-ink-mute truncate max-w-xs">{r.emails[0]}</div>
                </td>
                <td>{r.segment ?? '—'}</td>
                <td className="num">{formatAUD(r.tnw, { compact: true })}</td>
                <td className="num">{formatAUD(r.cash, { compact: true })}</td>
                <td>{formatDateAU(r.reviewDue)}</td>
                <td>{formatDateAU(r.lastFileNote)}</td>
                <td><ComplianceBadge state={r.compliance.riskProfile} label="Risk profile" /></td>
                <td><ComplianceBadge state={r.compliance.factFind} label="Fact find" /></td>
                <td><ComplianceBadge state={r.compliance.fileNote} label="File note" /></td>
                <td><ComplianceBadge state={r.compliance.bestInterest} label="Best interest" /></td>
                <td><ComplianceBadge state={r.compliance.soaSigned} label="SoA signed" /></td>
                <td><ComplianceBadge state={r.compliance.feeConsent} label="Fee consent" /></td>
                <td><ComplianceBadge state={r.compliance.writeSoa} label="Write SoA" /></td>
                <td><ComplianceBadge state={r.compliance.writeRoa} label="Write RoA" /></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={14}><QAEmpty inline title="No clients match this filter." /></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ label, k, view, onSort, right }: { label: string; k: string; view: View; onSort: (k: string) => void; right?: boolean }) {
  const active = view.sort.key === k;
  return (
    <th className={right ? 'text-right' : ''}>
      <button onClick={() => onSort(k)} className={`inline-flex items-center gap-1 ${active ? 'text-ink' : 'text-ink-mute'}`}>
        {label}
        {active && <span className="text-rust">{view.sort.dir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}
